/**
 * PropAI MUI Theme — structural defaults only.
 *
 * Defines PropAI palette augmentations, the semantic typography scale, and component
 * overrides (sizing, spacing, styling). Palette colors, font family, and font
 * weights are applied at runtime by theme.factory.ts from the branding system.
 */

import { createTheme } from "@mui/material";

import { defaultTheme as utilifeedTheme } from "./utilifeed.theme";

// Augment MUI palette to include custom PropAI tokens
// (Typography variant augmentations live in utilifeed.theme.ts, the shared base)
declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Palette {
    tertiary: Palette["primary"];
    inverseSurface: string;
    outline: Palette["primary"];
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
    inverseSurface?: string;
    outline?: PaletteOptions["primary"];
  }
}

// Typography variant base styles, fontFamily is a placeholder,
// overridden at runtime by theme.factory.ts with the branding API font.
const baseTypographyVariant = {
  fontStyle: "normal" as const,
  fontFamily: "'Public Sans Variable', Arial, sans-serif",
};

// Typography & Component Overrides
// Palette colors and font family are placeholders, they get overridden at runtime
// by theme.factory.ts using values from the branding system.
let baseTheme = createTheme(utilifeedTheme, {
  typography: {
    fontSize: 14,
    htmlFontSize: 14,
    h1: { ...baseTypographyVariant, fontWeight: 500, fontSize: "4.071rem" },
    h2: { ...baseTypographyVariant, fontWeight: 500 },
    h3: { ...baseTypographyVariant, fontWeight: 500 },
    h4: { ...baseTypographyVariant, fontWeight: 500 },
    h5: { ...baseTypographyVariant, fontWeight: 300 },
    h6: { ...baseTypographyVariant, fontWeight: 300 },
    subtitle1: { ...baseTypographyVariant, fontWeight: 300, fontSize: "2rem" },
    subtitle2: { ...baseTypographyVariant, fontWeight: 300, fontSize: "1rem", color: "#757575" },
    body1: { ...baseTypographyVariant, fontWeight: 300 },
    body2: { ...baseTypographyVariant, fontWeight: 300 },
    button: { ...baseTypographyVariant, fontWeight: 500 },
    caption: { ...baseTypographyVariant, fontWeight: 300 },
    overline: { ...baseTypographyVariant, fontWeight: 300 },

    // Semantic typography scale
    displayLarge: {
      ...baseTypographyVariant,
      fontWeight: 300,
      fontSize: "57px",
      lineHeight: "64px",
      letterSpacing: "-0.25px",
    },
    displaySmall: {
      ...baseTypographyVariant,
      fontWeight: 300,
      fontSize: "36px",
      lineHeight: "44px",
      letterSpacing: "0px",
    },
    titleLarge: {
      ...baseTypographyVariant,
      fontWeight: 300,
      fontSize: "22px",
      lineHeight: "28px",
      letterSpacing: "0px",
    },
    titleMedium: {
      ...baseTypographyVariant,
      fontWeight: 500,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.15px",
    },
    titleSmall: {
      ...baseTypographyVariant,
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.1px",
    },
    labelLarge: {
      ...baseTypographyVariant,
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.5px",
    },
    labelMedium: {
      ...baseTypographyVariant,
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: "16px",
      letterSpacing: "0.5px",
    },
    bodyLarge: {
      ...baseTypographyVariant,
      fontWeight: 300,
      fontSize: "16px",
      lineHeight: "24px",
      letterSpacing: "0.5px",
    },
    bodyMedium: {
      ...baseTypographyVariant,
      fontWeight: 300,
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0.25px",
    },
    bodySmall: {
      ...baseTypographyVariant,
      fontWeight: 300,
      fontSize: "12px",
      lineHeight: "16px",
      letterSpacing: "0.4px",
    },
  },
});

// Component Overrides
baseTheme = createTheme(baseTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        /* Let non-MUI content inherit the branded theme font, while leaving html/body as the CssBaseline font anchor. */
        p, span, div, td, th, li, a, label, input, textarea, select, button,
        :not(html):not(body):not(h1):not(h2):not(h3):not(h4):not(h5):not(strong):not(b) {
          font-family: inherit;
          font-weight: 300;
        }

        /* Scrollbar styling */
        ::-webkit-scrollbar, *::-webkit-scrollbar {
          width: 0.6em;
          height: 0.6em;
          border-radius: 0;
        }

        ::-webkit-scrollbar-track, *::-webkit-scrollbar-track {
          border-radius: 0;
          background-color: ${baseTheme.palette.background.default};
          box-shadow: none;
        }

        ::-webkit-scrollbar-thumb, *::-webkit-scrollbar-thumb {
          border-radius: 0;
          background-color: ${baseTheme.palette.primary.main};
        }

        ::-webkit-scrollbar-thumb:hover, *::-webkit-scrollbar-thumb:hover {
          background-color: ${baseTheme.palette.primary.main};
        }
      `,
    },
    MuiTypography: {
      defaultProps: {
        root: { color: baseTheme.palette.text.primary },
        body1: { color: baseTheme.palette.text.secondary },
        subtitle1: { color: baseTheme.palette.primary.dark },
      },
      styleOverrides: {
        body1: { color: baseTheme.palette.text.secondary },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minWidth: "120px",
          textTransform: "capitalize",
          color: baseTheme.palette.background.default,
          ...baseTheme.typography.labelLarge,
          "&.Mui-selected": { color: baseTheme.palette.background.default },
          "&.Mui-disabled": { color: baseTheme.palette.background.default, opacity: 0.5 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: baseTheme.palette.background.default },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontWeight: 300 },
        head: { fontWeight: 500, color: baseTheme.palette.primary.dark },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          maxWidth: 190,
          padding: baseTheme.spacing(1, 2),
          fontFamily: baseTheme.typography.body2.fontFamily,
          fontSize: 12,
          lineHeight: 1.3,
          fontWeight: baseTheme.typography.fontWeightMedium,
          color: baseTheme.palette.grey["50"],
          backgroundColor: baseTheme.palette.grey["600"],
        },
        tooltipPlacementRight: {
          transformOrigin: "left center",
          margin: "0 !important",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
  },
});

export const propaiTheme = baseTheme;
